import React, { useState } from 'react';
import { Box, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import { 
  getTaskItemTypeColor,
  getTaskItemTypeIcon
} from '../../utils/helper';
import { getTaskItemTypeLabel } from '../../utils/lead_helper_v2';

// Styled card component for week view
const WeekCardContainer = styled(Box)(({ theme, borderColor, statusColor }) => ({
  backgroundColor: '#FFFFFF',
  border: `1px solid ${borderColor || '#E2E8F0'}`,
  borderLeft: `3px solid ${statusColor || '#9CA3AF'}`,
  borderRadius: '4px',
  padding: '4px 6px',
  margin: '1px 0',
  minHeight: '24px',
  maxHeight: '28px',
  width: '100%',
  cursor: 'pointer',
  transition: 'all 0.2s ease',
  position: 'relative',
  boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)',
  overflow: 'hidden',
  '&:hover': {
    backgroundColor: '#F8FAFC',
    borderColor: '#CBD5E1',
    transform: 'translateY(-1px)',
    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
  },
}));

const WeekViewCard = ({ 
  item, 
  onClick, 
  showLead = true 
}) => {
  const [isHovered, setIsHovered] = useState(false);

  // Calculate completion percentage
  const completed = item.completed || 0;
  const total = item.total || item.count || 0;
  const completionPercentage = total > 0 ? Math.round((completed / total) * 100) : null;

  // Get color based on completion percentage
  const getCompletionColor = (percentage) => {
    if (percentage >= 90) return '#10B981'; // Green
    if (percentage >= 70) return '#F59E0B'; // Orange
    if (percentage >= 50) return '#EAB308'; // Yellow
    return '#EF4444'; // Red
  };

  const completionColor = completionPercentage !== null ? getCompletionColor(completionPercentage) : getTaskItemTypeColor(item.item_type).border;

  return (
    <WeekCardContainer 
      borderColor="#E2E8F0"
      statusColor={getTaskItemTypeColor(item.item_type).border}
      backgroundColor={getTaskItemTypeColor(item.item_type).background}
      onClick={() => onClick && onClick(item)}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      overflow="hidden"
    >
      {/* Display task type with count */}
      <Box sx={{
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'flex-start',
        alignItems: 'center',
        height: '100%',
        gap: 0.3
      }}>
        {/* Task Type Icon - smaller */}
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center',
          '& svg': {
            fontSize: '0.75rem'
          }
        }}>
          {getTaskItemTypeIcon(item.item_type)}
        </Box>
        
        {/* Task Type Text with Count */}
        <Typography
          sx={{
            fontSize: '0.625rem',
            fontWeight: 600,
            color: getTaskItemTypeColor(item.item_type).border,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            lineHeight: 1,
            textTransform: 'none',
            flex: 1
          }}
        >
          {getTaskItemTypeLabel(item.item_type, item.item_type_verbose)}
          {item.count && ` (${item.count})`}
        </Typography>

        {/* Completion percentage indicator */}
        {completionPercentage !== null && completionPercentage !== undefined && (
          <Typography
            sx={{
              fontSize: '0.5rem',
              fontWeight: 700,
              color: completionColor,
              ml: 0.5,
              lineHeight: 1,
              whiteSpace: 'nowrap'
            }}
          >
            {completionPercentage}%
          </Typography>
        )}
      </Box>

      {/* Progress bar at the bottom */}
      {completionPercentage !== null && completionPercentage !== undefined && (
        <Box
          sx={{
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            height: '3px',
            backgroundColor: 'rgba(0, 0, 0, 0.08)',
            borderRadius: '0 0 4px 4px'
          }}
        >
          <Box
            sx={{
              height: '100%',
              width: `${completionPercentage}%`,
              backgroundColor: completionColor,
              opacity: 0.9,
              transition: 'width 0.3s ease, background-color 0.3s ease',
              boxShadow: `0 0 6px ${completionColor}40`
            }}
          />
        </Box>
      )}
    </WeekCardContainer>
  );
};

export default WeekViewCard; 